import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, TextField, InputAdornment, Button,
  List, ListItemButton, ListItemIcon, ListItemText, Card,
  CardActionArea, CardMedia
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  ArticleOutlined as ArticleIcon,
  CheckCircleOutline as CanIcon,
  BlockOutlined as CantIcon,
  VolumeUpOutlined as ListenIcon,
  StopCircleOutlined as StopIcon,
  ChatBubbleOutline as CoachIcon,
  PlayCircleOutline as PlayIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import VideoEmbed from './VideoEmbed';
import { HELP_VIDEOS, thumbnailFor } from '../../config/helpVideos';
import { HELP_ARTICLES } from '../../help/helpArticles.generated';
import { MarkdownBody, toSpeech } from '../../help/HelpShared';
import { pageForPath } from '../../help/helpPages';
import { signalSearchNoResults, signalArticleEscalation } from '../../help/helpSignals';

/**
 * The context-aware Help panel (evolution of the Item 72 video hub).
 * Opened from the same "?" in the top bar, but now leads with the page the
 * user is standing on: what it is, what you can and can't do here, and the
 * guides bound to it — then search across every article, the two remaining
 * videos, and a hand-off to the Coach. Articles are authored as markdown in
 * Docs/help-articles and compiled in at build time.
 */

const HelpCenter = ({ open, onClose }) => {
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [query, setQuery] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const utteranceRef = useRef(null);

  const page = useMemo(
    () => pageForPath(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const pageArticles = useMemo(
    () => (page ? HELP_ARTICLES.filter((a) => a.screens.includes(page.key)) : []),
    [page]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return HELP_ARTICLES.filter((a) =>
      a.title.toLowerCase().includes(q) ||
      a.keywords.some((k) => k.toLowerCase().includes(q)) ||
      a.body.toLowerCase().includes(q)
    );
  }, [query]);

  const stopSpeech = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  const toggleSpeech = () => {
    if (!window.speechSynthesis || !article) return;
    if (speaking) { stopSpeech(); return; }
    const u = new SpeechSynthesisUtterance(toSpeech(article));
    u.rate = 1.0;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utteranceRef.current = u;
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  useEffect(() => () => stopSpeech(), []);
  useEffect(() => { if (!open) { setArticle(null); setQuery(''); stopSpeech(); } }, [open]);

  // Listening loop: a search that settles on zero results is a gap in the
  // corpus — log it (debounced so mid-typing misses don't count).
  useEffect(() => {
    if (!results || results.length > 0) return undefined;
    const t = setTimeout(() => signalSearchNoResults(query, page?.key), 1200);
    return () => clearTimeout(t);
  }, [results, query, page]);

  const handleClose = () => { onClose(); };

  const askCoach = () => {
    // Listening loop: reading an article and STILL needing the Coach means
    // the article under-delivered — that's the deepening queue.
    if (article) signalArticleEscalation(article.id, page?.key);
    const prompt = article
      ? `I was reading the help article "${article.title}" and still have a question.`
      : page
        ? `I'm on the ${page.title} page and need help understanding it.`
        : 'I need help understanding this page.';
    window.dispatchEvent(new CustomEvent('kodahfy:open-coach', { detail: { prompt } }));
    handleClose();
  };

  const ArticleRow = ({ a }) => (
    <ListItemButton onClick={() => { setArticle(a); stopSpeech(); }} sx={{ borderRadius: 1 }}>
      <ListItemIcon sx={{ minWidth: 34 }}><ArticleIcon fontSize="small" /></ListItemIcon>
      <ListItemText
        primary={a.title}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
        secondary={a.category}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItemButton>
  );

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, p: 0 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {article && (
          <IconButton size="small" onClick={() => { setArticle(null); stopSpeech(); }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }} noWrap>
          {article ? article.title : 'Help'}
        </Typography>
        {article && window.speechSynthesis && (
          <Button size="small" startIcon={speaking ? <StopIcon /> : <ListenIcon />} onClick={toggleSpeech}>
            {speaking ? 'Stop' : 'Listen'}
          </Button>
        )}
        <IconButton size="small" title="Open full page in a new tab"
          onClick={() => {
            const q = new URLSearchParams();
            if (article) q.set('article', article.id);
            if (page) q.set('from', page.key);
            window.open(`/help${q.toString() ? '?' + q.toString() : ''}`, '_blank');
          }}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {article ? (
          <MarkdownBody body={article.body} />
        ) : (
          <>
            {/* Search */}
            <TextField
              fullWidth size="small" placeholder="Search all help" value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ mb: 2 }}
            />

            {results ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {results.length ? `${results.length} result${results.length === 1 ? '' : 's'}` : 'No results'}
                </Typography>
                <List dense disablePadding sx={{ mt: 0.5 }}>
                  {results.map((a) => <ArticleRow key={a.id} a={a} />)}
                </List>
                {!results.length && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Nothing matched — the Coach can probably help.
                  </Typography>
                )}
              </>
            ) : (
              <>
                {/* About this page */}
                {page && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      About this page
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.25 }}>{page.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.6 }}>
                      {page.blurb}
                    </Typography>
                    {page.can.length > 0 && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>On this page you can</Typography>
                        {page.can.map((c, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 0.5 }}>
                            <CanIcon sx={{ fontSize: 16, color: 'success.main', mt: '2px' }} />
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{c}</Typography>
                          </Box>
                        ))}
                        {page.cant.map((c, i) => (
                          <Box key={`x${i}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 0.5 }}>
                            <CantIcon sx={{ fontSize: 16, color: 'text.disabled', mt: '2px' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{c}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Guides for this page */}
                {pageArticles.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Guides for this page
                    </Typography>
                    <List dense disablePadding sx={{ mt: 0.5, mb: 2 }}>
                      {pageArticles.map((a) => <ArticleRow key={a.id} a={a} />)}
                    </List>
                  </>
                )}

                {/* Videos */}
                {HELP_VIDEOS.filter((v) => v.youtubeId).length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Videos
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, mb: 2 }}>
                      {HELP_VIDEOS.filter((v) => v.youtubeId).map((v) => (
                        <VideoRow key={v.id} v={v} />
                      ))}
                    </Box>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Coach hand-off */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button fullWidth startIcon={<CoachIcon />} onClick={askCoach} variant="outlined">
          {page ? `Ask the Coach about ${page.title}` : 'Ask the Coach'}
        </Button>
      </Box>
    </Drawer>
  );
};

const VideoRow = ({ v }) => {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <Box>
        <VideoEmbed url={`https://youtu.be/${v.youtubeId}`} title={v.title} maxWidth={9999} />
        <Button size="small" onClick={() => setPlaying(false)} sx={{ mt: 0.5 }}>Close video</Button>
      </Box>
    );
  }
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardActionArea onClick={() => setPlaying(true)} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', flexShrink: 0, width: 120 }}>
          <CardMedia component="img" image={thumbnailFor(v.youtubeId)} alt={v.title}
            sx={{ aspectRatio: '16 / 9', objectFit: 'cover', bgcolor: '#0f172a' }} />
          <PlayIcon sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 28, color: 'rgba(255,255,255,0.92)' }} />
        </Box>
        <Box sx={{ px: 1.25, py: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.title}</Typography>
          <Typography variant="caption" color="text.secondary">{v.description}</Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default HelpCenter;
